import axios from 'axios';

export class IgApi {
  static cache = {};
  static useWorkerHubApi = true;

  /**
   * Get story by url
   * @param options {Object}
   * @param options.url {string} - url
   * @param options.subscribeSignedRequestBody {Function} - Function that returns signed request body
   * @param options.signal {AbortSignal} - Abort signal
   */
  static async getStory(options) {
    const { url, subscribeSignedRequestBody, signal } = options;

    if (this.useWorkerHubApi) {
      return await this.requestToWorkerHub({
        endpoint: 'story',
        body: {
          url,
        },
        subscribeSignedRequestBody,
        signal,
      });
    }

    return this.requestToIgStories({
      endpoint: `story?url=${encodeURIComponent(url)}`,
      signal,
    });
  }

  /**
   * Get stories by highlight id
   * @param options {Object}
   * @param options.highlightId {string} - highlightId
   * @param options.subscribeSignedRequestBody {Function} - Function that returns signed request body
   * @param [options.signal] {AbortSignal} - Abort signal
   */
  static async getHighlightStoriesById(options) {
    const { highlightId, subscribeSignedRequestBody, signal } = options;

    if (this.useWorkerHubApi) {
      return await this.requestToWorkerHub({
        endpoint: 'highlightStories',
        body: {
          highlightId,
        },
        subscribeSignedRequestBody,
        signal,
      });
    }

    return this.requestToIgStories({
      endpoint: `highlightStories/${highlightId}`,
      signal,
    });
  }

  /**
   * Request to worker hub
   * @param options {Object}
   * @param options.endpoint {'profile' | 'userInfo' | 'stories' | 'highlights' | 'story' | 'highlightStories' | 'usernameSuggestions' | 'posts' | 'reels' | (string & {})} - Endpoint
   * @param options.body {Record<string, any>} - Request body
   * @param options.subscribeSignedRequestBody {Function} - Function that returns signed request body
   * @param options.signal {AbortSignal} - Abort signal
   * @returns {AxiosResponse}
   */
  static async requestToWorkerHub(options) {
    const { endpoint, body, subscribeSignedRequestBody, signal } = options;

    const requestUrl = `/api/v1/instagram/${endpoint}`;

    if (this.cache[requestUrl + ':' + JSON.stringify(body)]) {
      return this.cache[requestUrl + ':' + JSON.stringify(body)];
    }

    if (!subscribeSignedRequestBody) {
      console.warn('subscribeSignedRequestBody is not defined');

      const response = await axios.post(requestUrl, body, {
        signal,
      });

      if (response.data?.success !== false) {
        this.cache[requestUrl + ':' + JSON.stringify(body)] = response;
      }

      return response;
    }

    const response = await axios.post(
      requestUrl,
      {
        ...(await subscribeSignedRequestBody(body)),
      },
      { signal }
    );

    if (response.data?.success !== false) {
      this.cache[requestUrl + ':' + JSON.stringify(body)] = response;
    }

    return response;
  }

  /**
   * Request to link
   * @param options {Object}
   * @param options.url {string} - URL
   * @param options.subscribeSignedRequestBody {Function} - Function that returns signed request body
   * @param options.signal {AbortSignal} - Abort signal
   */
  static async requestToLink(options) {
    const { url, subscribeSignedRequestBody, signal } = options;
    const requestUrl = `/api/convert`;

    if (this.cache[url]) {
      return this.cache[url];
    }

    if (!subscribeSignedRequestBody) {
      console.warn('subscribeSignedRequestBody is not defined');

      const response = await axios.post(
        requestUrl,
        {
          url,
        },
        {
          signal,
        }
      );

      this.cache[url] = response;

      return response;
    }

    const response = await axios.post(
      requestUrl,
      {
        ...(await subscribeSignedRequestBody(url)),
      },
      { signal }
    );

    this.cache[url] = response;

    return response;
  }

  /**
   * Request to ig stories worker
   * @param options {Object}
   * @param options.endpoint {string} - Endpoint
   * @param options.signal {AbortSignal} - Abort signal
   */
  static async requestToIgStories(options) {
    const { endpoint, signal } = options;
    const token = localStorage.getItem('token');
    const requestUrl = `/api/ig/${endpoint}`;

    if (this.cache[requestUrl]) {
      return this.cache[requestUrl];
    }

    const response = await axios.get(requestUrl, {
      signal,
      headers: {
        'x-token': token,
      },
    });

    this.cache[requestUrl] = response;

    return response;
  }
}
